import {
    Component,
    forwardRef,
    input,
    InputSignal,
    CUSTOM_ELEMENTS_SCHEMA,
    signal,
    WritableSignal,
    inject,
    OnDestroy,
    OnInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { UserService } from '../../services/user.service';
import { TeamService } from '../../services/team.service';
import { IUser, ITeam, Roles, BusinessServices } from '@shs-digi-care-monorepo/interfaces';
import {
    Subject,
    debounceTime,
    distinctUntilChanged,
    switchMap,
    of,
    catchError,
    forkJoin,
    map,
    tap,
    EMPTY,
    filter
} from 'rxjs';

export type { AssigneeType, AssigneeValue, AssigneeOption } from './model/sh-assignee-picker.model';
import type { AssigneeOption, AssigneeValue } from './model/sh-assignee-picker.model';

@Component({
    selector: 'sh-assignee-picker',
    templateUrl: './sh-assignee-picker.component.html',
    standalone: true,
    imports: [CommonModule, TranslateModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShAssigneePickerComponent),
            multi: true
        }
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShAssigneePickerComponent implements ControlValueAccessor, OnDestroy, OnInit {
    private readonly userService = inject(UserService);
    private readonly teamService = inject(TeamService);

    // Inputs
    public label: InputSignal<string> = input<string>('common.general.assignee');
    public roles: InputSignal<Roles[] | null> = input<Roles[] | null>(null);
    public disabled: InputSignal<boolean> = input<boolean>(false);
    public mandatory: InputSignal<boolean> = input<boolean>(false);
    public error: InputSignal<boolean> = input<boolean>(false);
    public placeholder: InputSignal<string> = input<string>('common.assignee-picker.placeholder');
    public businessService: InputSignal<BusinessServices | undefined> = input<BusinessServices | undefined>(undefined);
    public teamIndicator: InputSignal<string> = input<string>('nam.parallel-referral-dialog.form.team-indicator');
    public excludeIds: InputSignal<Set<string>> = input<Set<string>>(new Set());
    public optionTestId: InputSignal<string | undefined> = input<string | undefined>(undefined);

    // Internal state
    protected searchResults: WritableSignal<AssigneeOption[]> = signal([]);
    protected isLoading: WritableSignal<boolean> = signal(false);
    protected isDisabled: WritableSignal<boolean> = signal(false);
    protected searchTerm: WritableSignal<string> = signal('');
    protected selectedOption: WritableSignal<AssigneeOption | null> = signal(null);

    private initialResults: WritableSignal<AssigneeOption[]> = signal([]);
    private searchSubject = new Subject<string>();
    private onChange: (value: AssigneeValue | null) => void = () => {};
    private onTouched: () => void = () => {};

    public ngOnInit(): void {
        this.initSearchSubject();
        this.loadInitialOptions();
    }

    private loadInitialOptions(): void {
        this.searchBoth('')
            .pipe(
                tap((results) => {
                    this.initialResults.set(results);
                    this.searchResults.set(results);
                })
            )
            .subscribe();
    }

    private initSearchSubject(): void {
        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term) => {
                    this.isLoading.set(true);
                    return this.searchBoth(term);
                }),
                tap((results) => {
                    this.searchResults.set(results);
                    this.isLoading.set(false);
                })
            )
            .subscribe();
    }

    private searchBoth(term: string) {
        const users$ = this.userService.getUsers(0, 10, term || undefined).pipe(
            map((result) =>
                (result.data || []).map(
                    (user: IUser): AssigneeOption => ({
                        type: 'user',
                        id: user.gid,
                        label: `${user.firstName} ${user.lastName}`.trim() || user.email || user.gid,
                        email: user.email
                    })
                )
            ),
            catchError(() => of([]))
        );

        const teams$ = this.teamService.getTeams(0, 100, this.businessService()).pipe(
            map((result) => {
                const lower = (term || '').toLowerCase();
                return (result.items || [])
                    .filter((t: ITeam) => !lower || t.name.toLowerCase().includes(lower))
                    .map(
                        (team: ITeam): AssigneeOption => ({
                            type: 'team',
                            id: team.id,
                            label: team.name
                        })
                    );
            }),
            catchError(() => of([]))
        );

        return forkJoin([teams$, users$]).pipe(
            map(([teams, users]) => {
                const excluded = this.excludeIds();
                const all = [...teams, ...users];
                return excluded.size > 0
                    ? all.filter((o) => !excluded.has(o.id) && !(o.email && excluded.has(o.email)))
                    : all;
            }),
            catchError(() => of([]))
        );
    }

    // ControlValueAccessor
    public writeValue(value: AssigneeValue | null): void {
        if (!value) {
            this.selectedOption.set(null);
            return;
        }

        // If the value already carries a label, restore the selection synchronously
        if (value.label) {
            this.selectedOption.set({
                type: value.type,
                id: value.id,
                label: value.label,
                email: value.email
            });
            return;
        }

        // Fallback: load the display name from the server
        if (value.type === 'user') {
            this.userService
                .getByGID(value.id)
                .pipe(
                    filter((user) => !!user?.gid),
                    tap((user) => {
                        this.selectedOption.set({
                            type: 'user',
                            id: user.gid,
                            label: `${user.firstName} ${user.lastName}`.trim() || user.email || user.gid,
                            email: user.email
                        });
                    }),
                    catchError(() => {
                        this.selectedOption.set(null);
                        return EMPTY;
                    })
                )
                .subscribe();
        } else {
            this.teamService
                .getTeamById(value.id)
                .pipe(
                    filter((team) => !!team?.id),
                    tap((team) => {
                        this.selectedOption.set({ type: 'team', id: team.id, label: team.name });
                    }),
                    catchError(() => {
                        this.selectedOption.set(null);
                        return EMPTY;
                    })
                )
                .subscribe();
        }
    }

    public registerOnChange(fn: (value: AssigneeValue | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    protected handleSearchInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const term = input.value;
        this.searchTerm.set(term);
        if (!term) {
            this.searchResults.set(this.filterExcluded(this.initialResults()));
            return;
        }
        this.searchSubject.next(term);
    }

    protected handleSelect(option: AssigneeOption): void {
        this.selectedOption.set(option);
        this.searchTerm.set('');
        this.searchResults.set(this.initialResults());
        this.onChange({ type: option.type, id: option.id, email: option.email, label: option.label });
        this.onTouched();
    }

    protected handleClear(): void {
        this.selectedOption.set(null);
        this.searchTerm.set('');
        this.searchResults.set(this.initialResults());
        this.onChange(null);
        this.onTouched();
    }

    public ngOnDestroy(): void {
        this.searchSubject.complete();
    }

    private filterExcluded(options: AssigneeOption[]): AssigneeOption[] {
        const excluded = this.excludeIds();
        if (excluded.size === 0) return options;
        return options.filter((o) => !excluded.has(o.id) && !(o.email && excluded.has(o.email)));
    }
}
